import type {
  AcademicGrades,
  CoachSupport,
  LearnDocumentation,
  MethodologicalCompetence,
  PastSemesterGoals,
  ProfessionalCompetence,
  ReceivedTraining,
  SelfAssessmentData,
  SelfCompetence,
  SocialCompetence,
} from "../../../models/reports/educationReport";
import type { EducationReportTranslations } from "../../../models/reports/educationReportTranslations";
import type { GradingField } from "./GradingSection";

export function getProfessionalFields(
  translations: EducationReportTranslations,
  selfAssessment?: SelfAssessmentData["professional_competence"],
): GradingField<ProfessionalCompetence>[] {
  return [
    {
      title: translations.professional_competence.training_level.title,
      description: translations.professional_competence.training_level.description,
      key: "training_level",
      selfAssessment: selfAssessment?.training_level,
    },
    {
      title: translations.professional_competence.work_quality.title,
      description: translations.professional_competence.work_quality.description,
      key: "work_quality",
      selfAssessment: selfAssessment?.work_quality,
    },
    {
      title: translations.professional_competence.work_amount.title,
      description: translations.professional_competence.work_amount.description,
      key: "work_amount",
      selfAssessment: selfAssessment?.work_amount,
    },
    {
      title: translations.professional_competence.knowledge_implementation.title,
      description: translations.professional_competence.knowledge_implementation.description,
      key: "knowledge_implementation",
      selfAssessment: selfAssessment?.knowledge_implementation,
    },
  ];
}

export function getMethodologicalFields(
  translations: EducationReportTranslations,
  selfAssessment?: SelfAssessmentData["methodological_competence"],
): GradingField<MethodologicalCompetence>[] {
  return [
    {
      title: translations.methodological_competence.work_technique.title,
      description: translations.methodological_competence.work_technique.description,
      key: "work_technique",
      selfAssessment: selfAssessment?.work_technique,
    },
    {
      title: translations.methodological_competence.networked_thinking.title,
      description: translations.methodological_competence.networked_thinking.description,
      key: "networked_thinking",
      selfAssessment: selfAssessment?.networked_thinking,
    },
    {
      title: translations.methodological_competence.resource_handling.title,
      description: translations.methodological_competence.resource_handling.description,
      key: "resource_handling",
      selfAssessment: selfAssessment?.resource_handling,
    },
    {
      title: translations.methodological_competence.work_strategy.title,
      description: translations.methodological_competence.work_strategy.description,
      key: "work_strategy",
      selfAssessment: selfAssessment?.work_strategy,
    },
  ];
}

export function getSocialFields(
  translations: EducationReportTranslations,
  selfAssessment?: SelfAssessmentData["social_competence"],
): GradingField<SocialCompetence>[] {
  return [
    {
      title: translations.social_competence.teamwork_skills.title,
      description: translations.social_competence.teamwork_skills.description,
      key: "teamwork_skills",
      selfAssessment: selfAssessment?.teamwork_skills,
    },
    {
      title: translations.social_competence.cooperation.title,
      description: translations.social_competence.cooperation.description,
      key: "cooperation",
      selfAssessment: selfAssessment?.cooperation,
    },
    {
      title: translations.social_competence.communication.title,
      description: translations.social_competence.communication.description,
      key: "communication",
      selfAssessment: selfAssessment?.communication,
    },
    {
      title: translations.social_competence.customer_oriented_action.title,
      description: translations.social_competence.customer_oriented_action.description,
      key: "customer_oriented_action",
      selfAssessment: selfAssessment?.customer_oriented_action,
    },
  ];
}

export function getSelfFields(
  translations: EducationReportTranslations,
  selfAssessment?: SelfAssessmentData["self_competence"],
): GradingField<SelfCompetence>[] {
  return [
    {
      title: translations.self_competence.independence.title,
      description: translations.self_competence.independence.description,
      key: "independence",
      selfAssessment: selfAssessment?.independence,
    },
    {
      title: translations.self_competence.reliability.title,
      description: translations.self_competence.reliability.description,
      key: "reliability",
      selfAssessment: selfAssessment?.reliability,
    },
    {
      title: translations.self_competence.manners.title,
      description: translations.self_competence.manners.description,
      key: "manners",
      selfAssessment: selfAssessment?.manners,
    },
    {
      title: translations.self_competence.motivation.title,
      description: translations.self_competence.motivation.description,
      key: "motivation",
      selfAssessment: selfAssessment?.motivation,
    },
  ];
}

export function getDocumentationFields(translations: EducationReportTranslations): GradingField<LearnDocumentation>[] {
  return [
    {
      title: translations.learn_documentation.factual_accuracy.title,
      key: "factual_accuracy",
    },
    {
      title: translations.learn_documentation.presentation_quality.title,
      key: "presentation_quality",
    },
  ];
}

export function getAcademicGradeFields(translations: EducationReportTranslations): GradingField<AcademicGrades>[] {
  return [
    {
      title: translations.academic_grades.semester_grades.title,
      key: "semester_grades",
    },
    {
      title: translations.academic_grades.uek_grades.title,
      key: "uek_grades",
    },
    {
      title: translations.academic_grades.courses_grades.title,
      key: "courses_grades",
    },
  ];
}

export function getReceivedTrainingFields(
  translations: EducationReportTranslations,
  selfAssessment?: SelfAssessmentData["apprentice_assessment"]["received_training"],
): GradingField<ReceivedTraining>[] {
  return [
    {
      title: translations.apprentice_assessment.received_training.professional_competence.title,
      key: "professional_competence",
      selfAssessment: selfAssessment?.professional_competence,
    },
    {
      title: translations.apprentice_assessment.received_training.methodological_competence.title,
      key: "methodological_competence",
      selfAssessment: selfAssessment?.methodological_competence,
    },
    {
      title: translations.apprentice_assessment.received_training.work_atmosphere.title,
      key: "work_atmosphere",
      selfAssessment: selfAssessment?.work_atmosphere,
    },
    {
      title: translations.apprentice_assessment.received_training.personal_support.title,
      key: "personal_support",
      selfAssessment: selfAssessment?.personal_support,
    },
  ];
}

export function getCoachSupportFields(
  selfAssessment?: SelfAssessmentData["apprentice_assessment"]["coach_support"],
): GradingField<CoachSupport>[] {
  return [
    {
      title: "",
      key: "support",
      selfAssessment: selfAssessment?.support,
    },
  ];
}

export function getPastSemesterFields(translations: EducationReportTranslations): GradingField<PastSemesterGoals>[] {
  return [
    {
      title: translations.past_semester_review.company_goals.title,
      key: "company_goals",
    },
    {
      title: translations.past_semester_review.educational_goals.title,
      key: "educational_goals",
    },
    {
      title: translations.past_semester_review.uek_goals.title,
      key: "uek_goals",
    },
    {
      title: translations.past_semester_review.professional_competence.title,
      key: "professional_competence",
    },
    {
      title: translations.past_semester_review.methodological_competence.title,
      key: "methodological_competence",
    },
    {
      title: translations.past_semester_review.social_competence.title,
      key: "social_competence",
    },
    {
      title: translations.past_semester_review.self_competence.title,
      key: "self_competence",
    },
  ];
}
